export const TETRIS_WIDTH = 10;
export const TETRIS_HEIGHT = 20;

export const TETRIS_PIECE_TYPES = ["I", "J", "L", "O", "S", "T", "Z"] as const;

export type TetrisPieceType = (typeof TETRIS_PIECE_TYPES)[number];
export type TetrisCell = TetrisPieceType | null;
export type TetrisBoard = TetrisCell[][];
export type TetrisStatus = "playing" | "paused" | "lost";

export type TetrisPiece = {
  type: TetrisPieceType;
  rotation: number;
  x: number;
  y: number;
};

export type TetrisState = {
  board: TetrisBoard;
  active: TetrisPiece;
  next: TetrisPieceType;
  score: number;
  lines: number;
  level: number;
  status: TetrisStatus;
};

type Shape = readonly (readonly [number, number])[];

const TETRIS_SHAPES = {
  I: [
    [
      [0, 1],
      [1, 1],
      [2, 1],
      [3, 1],
    ],
    [
      [2, 0],
      [2, 1],
      [2, 2],
      [2, 3],
    ],
  ],
  J: [
    [
      [0, 0],
      [0, 1],
      [1, 1],
      [2, 1],
    ],
    [
      [1, 0],
      [2, 0],
      [1, 1],
      [1, 2],
    ],
    [
      [0, 1],
      [1, 1],
      [2, 1],
      [2, 2],
    ],
    [
      [1, 0],
      [1, 1],
      [0, 2],
      [1, 2],
    ],
  ],
  L: [
    [
      [2, 0],
      [0, 1],
      [1, 1],
      [2, 1],
    ],
    [
      [1, 0],
      [1, 1],
      [1, 2],
      [2, 2],
    ],
    [
      [0, 1],
      [1, 1],
      [2, 1],
      [0, 2],
    ],
    [
      [0, 0],
      [1, 0],
      [1, 1],
      [1, 2],
    ],
  ],
  O: [
    [
      [1, 0],
      [2, 0],
      [1, 1],
      [2, 1],
    ],
  ],
  S: [
    [
      [1, 0],
      [2, 0],
      [0, 1],
      [1, 1],
    ],
    [
      [1, 0],
      [1, 1],
      [2, 1],
      [2, 2],
    ],
  ],
  T: [
    [
      [1, 0],
      [0, 1],
      [1, 1],
      [2, 1],
    ],
    [
      [1, 0],
      [1, 1],
      [2, 1],
      [1, 2],
    ],
    [
      [0, 1],
      [1, 1],
      [2, 1],
      [1, 2],
    ],
    [
      [1, 0],
      [0, 1],
      [1, 1],
      [1, 2],
    ],
  ],
  Z: [
    [
      [0, 0],
      [1, 0],
      [1, 1],
      [2, 1],
    ],
    [
      [2, 0],
      [1, 1],
      [2, 1],
      [1, 2],
    ],
  ],
} satisfies Record<TetrisPieceType, readonly Shape[]>;

const LINE_SCORE = [0, 100, 300, 500, 800] as const;

export function createEmptyTetrisBoard(): TetrisBoard {
  return Array.from({ length: TETRIS_HEIGHT }, () => Array<TetrisCell>(TETRIS_WIDTH).fill(null));
}

export function randomTetrisType(): TetrisPieceType {
  return TETRIS_PIECE_TYPES[Math.floor(Math.random() * TETRIS_PIECE_TYPES.length)];
}

export function createTetrisPiece(type: TetrisPieceType): TetrisPiece {
  return {
    type,
    rotation: 0,
    x: Math.floor(TETRIS_WIDTH / 2) - 2,
    y: 0,
  };
}

export function createTetrisState(activeType: TetrisPieceType = randomTetrisType(), nextType: TetrisPieceType = randomTetrisType()): TetrisState {
  return {
    board: createEmptyTetrisBoard(),
    active: createTetrisPiece(activeType),
    next: nextType,
    score: 0,
    lines: 0,
    level: 1,
    status: "playing",
  };
}

export function getTetrisDropDelay(level: number): number {
  return Math.max(145, 820 - Math.max(0, level - 1) * 58);
}

export function getTetrisPieceCells(piece: TetrisPiece) {
  const rotations = TETRIS_SHAPES[piece.type];
  const shape = rotations[piece.rotation % rotations.length];
  return shape.map(([x, y]) => ({
    x: piece.x + x,
    y: piece.y + y,
    type: piece.type,
  }));
}

export function isTetrisCollision(board: TetrisBoard, piece: TetrisPiece): boolean {
  return getTetrisPieceCells(piece).some(({ x, y }) => {
    if (x < 0 || x >= TETRIS_WIDTH || y >= TETRIS_HEIGHT) {
      return true;
    }
    return y >= 0 && Boolean(board[y]?.[x]);
  });
}

export function getTetrisVisibleBoard(state: TetrisState): TetrisBoard {
  const visible = state.board.map((row) => row.slice());
  getTetrisPieceCells(state.active).forEach(({ x, y, type }) => {
    if (x >= 0 && x < TETRIS_WIDTH && y >= 0 && y < TETRIS_HEIGHT) {
      visible[y][x] = type;
    }
  });
  return visible;
}

export function getTetrisPreviewCells(type: TetrisPieceType): boolean[][] {
  const preview = Array.from({ length: 4 }, () => Array<boolean>(4).fill(false));
  TETRIS_SHAPES[type][0].forEach(([x, y]) => {
    preview[y][x] = true;
  });
  return preview;
}

export function moveTetrisPiece(state: TetrisState, dx: number, dy: number): TetrisState {
  if (state.status !== "playing") {
    return state;
  }
  const active = { ...state.active, x: state.active.x + dx, y: state.active.y + dy };
  return isTetrisCollision(state.board, active) ? state : { ...state, active };
}

export function rotateTetrisPiece(state: TetrisState): TetrisState {
  if (state.status !== "playing") {
    return state;
  }
  const rotations = TETRIS_SHAPES[state.active.type].length;
  const rotated = { ...state.active, rotation: (state.active.rotation + 1) % rotations };
  const kicks = [0, -1, 1, -2, 2];
  for (const offset of kicks) {
    const active = { ...rotated, x: rotated.x + offset };
    if (!isTetrisCollision(state.board, active)) {
      return { ...state, active };
    }
  }
  return state;
}

export function dropTetrisPiece(state: TetrisState, pickNext: () => TetrisPieceType = randomTetrisType): TetrisState {
  if (state.status !== "playing") {
    return state;
  }
  const active = { ...state.active, y: state.active.y + 1 };
  return isTetrisCollision(state.board, active) ? lockTetrisPiece(state, pickNext) : { ...state, active };
}

export function hardDropTetrisPiece(state: TetrisState, pickNext: () => TetrisPieceType = randomTetrisType): TetrisState {
  if (state.status !== "playing") {
    return state;
  }
  let active = state.active;
  let distance = 0;
  while (!isTetrisCollision(state.board, { ...active, y: active.y + 1 })) {
    active = { ...active, y: active.y + 1 };
    distance += 1;
  }
  return lockTetrisPiece({ ...state, active, score: state.score + distance * 2 }, pickNext);
}

export function toggleTetrisPause(state: TetrisState): TetrisState {
  if (state.status === "lost") {
    return createTetrisState();
  }
  return { ...state, status: state.status === "paused" ? "playing" : "paused" };
}

export function serializeTetrisState(state: TetrisState) {
  return {
    status: state.status,
    score: state.score,
    lines: state.lines,
    level: state.level,
    active: state.active,
    next: state.next,
    lockedCells: state.board.reduce((total, row) => total + row.filter(Boolean).length, 0),
  };
}

function lockTetrisPiece(state: TetrisState, pickNext: () => TetrisPieceType): TetrisState {
  const merged = state.board.map((row) => row.slice());
  getTetrisPieceCells(state.active).forEach(({ x, y, type }) => {
    if (x >= 0 && x < TETRIS_WIDTH && y >= 0 && y < TETRIS_HEIGHT) {
      merged[y][x] = type;
    }
  });

  const { board, cleared } = clearTetrisLines(merged);
  const lines = state.lines + cleared;
  const level = Math.floor(lines / 10) + 1;
  const active = createTetrisPiece(state.next);
  const next = pickNext();
  const score = state.score + LINE_SCORE[cleared] * state.level;
  const status: TetrisStatus = isTetrisCollision(board, active) ? "lost" : "playing";

  return {
    ...state,
    board,
    active,
    next,
    score,
    lines,
    level,
    status,
  };
}

function clearTetrisLines(board: TetrisBoard): { board: TetrisBoard; cleared: number } {
  const remaining = board.filter((row) => row.some((cell) => cell === null));
  const cleared = TETRIS_HEIGHT - remaining.length;
  return {
    board: [...Array.from({ length: cleared }, () => Array<TetrisCell>(TETRIS_WIDTH).fill(null)), ...remaining],
    cleared,
  };
}
